import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuPortal,
  ContextMenuTrigger,
} from "@radix-ui/react-context-menu";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PiInputfield from "../../components/PiInputField";
import InteractionContextMenuItem from "./InteractionContextMenuItem";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSelectedElementIsInteraction,
  selectSelectedInteractionIds,
  selectSelectedInteractions,
  setInteractionsDisabled,
} from "../../app/slices/TrainingSlice";
import { TimelineClipboardContext } from "../training_timeline/CopyPasteContext";
import { useDeleteInteraction } from "../../hooks/DeleteInteractionHook";
import Interaction from "../../models/interactions/Interaction";
import type { QuizFeedbackSlot } from "../../models/Quiz";
import useInteractionContextMenu, {
  InteractionContextMenuOptions,
} from "./InteractionContextMenu.hook";

// A single icon-only action button for the horizontal action bar. The label
// lives in the tooltip and aria-label; a small count badge appears for group
// operations ("copy 3").
function ContextMenuActionIcon(props: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  tone?: "default" | "danger";
  badgeCount?: number;
  className?: string;
  testId: string;
}) {
  const isDanger = props.tone === "danger";
  return (
    <Tooltip title={props.label} placement="top" arrow>
      <button
        type="button"
        aria-label={props.label}
        data-testid={props.testId}
        className={`relative flex h-[32px] w-[32px] items-center justify-center rounded-[5px] text-[18px] outline-none hover:bg-creator-primary-washed ${
          isDanger ? "text-creator-error" : "text-creator-text-sub"
        } ${props.className ?? ""}`}
        onClick={props.onClick}
      >
        {props.icon}
        {(props.badgeCount ?? 0) > 1 && (
          <span
            className="absolute -right-1 -top-1 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-creator-primary px-[3px] text-[9px] font-semibold text-white"
            data-testid={`${props.testId}-count`}
          >
            {props.badgeCount}
          </span>
        )}
      </button>
    </Tooltip>
  );
}

// Fixed clipboard/delete action rows shown above the quick-add picker.
// IMPORTANT: these are plain <button>s, NOT Radix ContextMenuItems — Radix
// roving focus / typeahead would otherwise steal focus from the search input
// and break its type-to-filter UX.
export function ContextMenuActions(props: {
  pasteDestination?: { quizId: string; slot: QuizFeedbackSlot };
  closeMenu: () => void;
}) {
  const { pasteDestination, closeMenu } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const clipboardActions = useContext(TimelineClipboardContext);
  const isSelectedElementInteraction = useSelector(
    selectSelectedElementIsInteraction
  );
  const selectedElement = useSelector(selectSelectedElement);
  const selectionCount = useSelector(selectSelectedInteractionIds).length;
  const selectedInteractions = useSelector(selectSelectedInteractions);
  const [deleteInteraction, deleteInteractions] = useDeleteInteraction();

  if (!clipboardActions) return null;

  const showSelectionActions = isSelectedElementInteraction && selectedElement;
  const isGroupTarget = selectionCount > 1;
  const countLabel = (label: string) =>
    isGroupTarget ? `${label} ${selectionCount}` : label;

  const runAndClose = (action: () => void | Promise<void>) => () => {
    closeMenu();
    void action();
  };

  const removeSelection = () => {
    if (isGroupTarget) {
      deleteInteractions(selectedInteractions);
      return;
    }
    deleteInteraction(selectedElement as Interaction);
  };

  const targetInteractions: Interaction[] =
    selectedInteractions.length > 0
      ? selectedInteractions
      : showSelectionActions
        ? [selectedElement as Interaction]
        : [];
  // Mixed selections disable everything first; a second use re-enables.
  const anyEnabled = targetInteractions.some(
    (interaction) => !interaction.disabled
  );
  const toggleDisabled = () => {
    dispatch(
      setInteractionsDisabled({
        ids: targetInteractions.map((interaction) => interaction.id),
        disabled: anyEnabled,
      })
    );
  };

  return (
    <div className="flex items-center gap-[4px] border-b border-creator-border pb-[8px] -mb-[4px]">
      {showSelectionActions && (
        <ContextMenuActionIcon
          label={countLabel(t("copy"))}
          icon={<ContentCopyOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={runAndClose(clipboardActions.handleCopy)}
          badgeCount={selectionCount}
          testId="interaction-context-menu-copy"
        />
      )}
      {showSelectionActions && (
        <ContextMenuActionIcon
          label={countLabel(t("cut"))}
          icon={<ContentCutOutlinedIcon color="inherit" fontSize="inherit" />}
          onClick={runAndClose(clipboardActions.handleCut)}
          badgeCount={selectionCount}
          testId="interaction-context-menu-cut"
        />
      )}
      <ContextMenuActionIcon
        label={t("paste")}
        icon={<ContentPasteOutlinedIcon color="inherit" fontSize="inherit" />}
        onClick={runAndClose(() =>
          pasteDestination
            ? clipboardActions.handlePasteIntoSlot(
                pasteDestination.quizId,
                pasteDestination.slot
              )
            : clipboardActions.handlePaste()
        )}
        testId="interaction-context-menu-paste"
      />
      {showSelectionActions && (
        <ContextMenuActionIcon
          label={countLabel(anyEnabled ? t("disable") : t("enable"))}
          icon={
            anyEnabled ? (
              <VisibilityOffOutlinedIcon color="inherit" fontSize="inherit" />
            ) : (
              <VisibilityOutlinedIcon color="inherit" fontSize="inherit" />
            )
          }
          onClick={runAndClose(toggleDisabled)}
          badgeCount={selectionCount}
          testId="interaction-context-menu-toggle-disabled"
        />
      )}
      {showSelectionActions && (
        <ContextMenuActionIcon
          label={countLabel(t("delete"))}
          icon={
            <DeleteOutlineOutlinedIcon color="inherit" fontSize="inherit" />
          }
          onClick={runAndClose(removeSelection)}
          tone="danger"
          badgeCount={selectionCount}
          // destructive action sits apart from the clipboard cluster
          className="ml-auto"
          testId="interaction-context-menu-delete"
        />
      )}
    </div>
  );
}

export default function InteractionContextMenu(props: {
  children: React.ReactNode;
  pasteDestination?: { quizId: string; slot: QuizFeedbackSlot };
} & InteractionContextMenuOptions) {
  const {
    filteredDefinitions,
    onSelectInteraction,
    selectedIndex,
    setFilter,
    onOpenChange,
    filteredPropInteractions,
    closeMenu,
  } = useInteractionContextMenu({
    definitions: props.definitions,
    onAddInteraction: props.onAddInteraction,
  });

  var globalIndex = -1;

  const definitionsMapped = useMemo(() => {
    const normalInteractions = filteredDefinitions.map((def, index) => {
      globalIndex += 1;
      return (
        <InteractionContextMenuItem
          key={def.name}
          interactionDefinition={def}
          onSelect={onSelectInteraction}
          isSelected={globalIndex === selectedIndex}
        />
      );
    });

    // const propInteractions = Object.entries(filteredPropInteractions).map(
    //   ([key, interactions]) => {
    //     return interactions.map((interaction, index) => {
    //       globalIndex += 1;
    //       return (
    //         <InteractionContextMenuItem
    //           key={interaction.name}
    //           interactionDefinition={interaction}
    //           onSelect={onSelectInteraction}
    //           isSelected={globalIndex === selectedIndex}
    //           propInstanceId={key}
    //         />
    //       );
    //     });
    //   }
    // );

    return [...normalInteractions];
  }, [filteredDefinitions, selectedIndex]);

  const { children } = { ...props };

  return (
    <ContextMenu modal={true} onOpenChange={onOpenChange}>
      <ContextMenuTrigger className="w-full" asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuPortal>
        <ContextMenuContent
          className="bg-creator-surface flex flex-col gap-[12px] shadow-md rounded-[5px] border-2 border-solid border-creator-border px-[8px] py-[16px] max-h-[440px] h-auto"
          style={{
            pointerEvents: "auto", // <-- important
            zIndex: 1000, // ensure it's above the trigger
          }}
        >
          <ContextMenuActions
            pasteDestination={props.pasteDestination}
            closeMenu={closeMenu}
          />
          <div className="">
            <PiInputfield
              onChange={setFilter}
              label=""
              placeholder="Search"
              autoFocus
              debounceTime={30}
            />
          </div>
          <div className="h-[210px] w-[256px] overflow-y-auto flex flex-col gap-[8px] pit-creator-scrollbar overflow-x-hidden">
            {definitionsMapped}
          </div>
        </ContextMenuContent>
      </ContextMenuPortal>
    </ContextMenu>
  );
}
